import { get as lodashGet } from "lodash";
import { useMemo } from "react";
import { type ReviewSessionVotingPageChildTab } from "shared/api/types/society/[societyId]/review/session";
import { renderTemplateUtil } from "shared/components/template/renderTemplateUtil";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type ReviewInstanceAssignmentView } from "shared/mappers/database/review/reviewInstanceAssignment";
import { type ReviewSessionView } from "shared/mappers/database/review/reviewSession";
import { type SubmissionPreviewProps } from "hub/src/components/reviews/HubReviewsSubmissionPreview";

export type HubReviewsVotingChildTab = {
  config: ReviewSessionVotingPageChildTab;
  templateData: Record<string, unknown>;
};

export type HubReviewsVotingTab =
  | { kind: "submission"; submission: SubmissionPreviewProps }
  | { kind: "child"; child: HubReviewsVotingChildTab };

export const useHubReviewsVotingTabsArray = (
  response: ReviewInstanceAssignmentView | null,
  reviewSession: ReviewSessionView | undefined,
): HubReviewsVotingTab[] => {
  const session = useCustomSession();

  return useMemo(() => {
    if (!response?.reviewInstance?.workflowInstance) {
      return [];
    }

    const reviewTabs: HubReviewsVotingTab[] = [];

    // Primary submission tab
    reviewTabs.push({
      kind: "submission",
      submission: {
        workflowInstance: response.reviewInstance.workflowInstance,
        submissionInstance:
          response.reviewInstance.workflowInstance.submissionInstance,
        submitterProfile:
          response.reviewInstance.workflowInstance.submissionInstance
            ?.primaryProfile ??
          response.reviewInstance.workflowInstance.profile!,
        disclosureInstance: response.reviewInstance?.disclosureInstance,
      },
    });

    // Parent (if exists)
    const parentSubmission =
      response.reviewInstance?.workflowInstance?.parentWorkflowInstance
        ?.submissionInstance;

    if (parentSubmission) {
      reviewTabs.push({
        kind: "submission",
        submission: {
          submissionInstance: parentSubmission,
          submitterProfile: parentSubmission.primaryProfile!,
        },
      });
    }

    // Configured child tabs, one per element of each arrayPath
    const rootData = {
      reviewInstance: response.reviewInstance,
      reviewSession,
      session,
    };

    for (const config of reviewSession?.additionalInfo?.votingPageConfig
      ?.childTabs ?? []) {
      const elements: unknown = lodashGet(rootData, config.arrayPath);

      if (!Array.isArray(elements)) {
        continue;
      }

      for (const element of elements) {
        const templateData = { ...rootData, element };

        if (
          config.visibleIf &&
          renderTemplateUtil({
            template: config.visibleIf,
            templateData,
          }).trim() !== "true"
        ) {
          continue;
        }

        reviewTabs.push({ kind: "child", child: { config, templateData } });
      }
    }

    return reviewTabs;
  }, [response, reviewSession, session]);
};
